/* Aura Voice helpers — MediaRecorder + Web Audio for cross-browser (incl. iOS Safari) */

// Pick the best supported MIME type. iOS Safari only supports mp4/aac.
export function getSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4;codecs=mp4a.40.2", // iOS Safari
    "audio/mp4",
    "audio/aac",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return ""; // empty = browser default
}

/**
 * Start recording from the mic.
 * Returns { stop: () => Promise<Blob>, cancel: () => void, stream }
 */
export async function startRecording() {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
  });
  const mimeType = getSupportedMimeType();
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  const chunks = [];
  recorder.addEventListener("dataavailable", (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });
  recorder.start();

  const stop = () =>
    new Promise((resolve) => {
      recorder.addEventListener("stop", () => {
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        stream.getTracks().forEach((t) => t.stop());
        resolve(blob);
      }, { once: true });
      if (recorder.state !== "inactive") recorder.stop();
    });

  const cancel = () => {
    if (recorder.state !== "inactive") recorder.stop();
    stream.getTracks().forEach((t) => t.stop());
  };

  return { stop, cancel, stream, recorder, mimeType: mimeType || recorder.mimeType };
}

/**
 * Voice Activity Detector (lightweight).
 * Calls `onSilence` after `silenceMs` of continuous quiet (below `threshold`).
 * Returns a stop() function.
 */
export function attachSilenceDetector(stream, { threshold = 0.015, silenceMs = 1400, onSilence, onLevel } = {}) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return () => {};
  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const data = new Uint8Array(analyser.fftSize);

  let silenceStart = null;
  let stopped = false;
  let raf;

  const tick = () => {
    if (stopped) return;
    analyser.getByteTimeDomainData(data);
    // RMS amplitude in 0..1
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / data.length);
    if (onLevel) onLevel(rms);

    if (rms < threshold) {
      if (silenceStart === null) silenceStart = performance.now();
      else if (performance.now() - silenceStart >= silenceMs) {
        stopped = true;
        if (onSilence) onSilence();
        return;
      }
    } else {
      silenceStart = null;
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return () => {
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
    try { ctx.close(); } catch (_) {}
  };
}

/** Play an MP3 base64 string. Resolves when playback ends. */
export function playBase64Mp3(base64) {
  return new Promise((resolve, reject) => {
    try {
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      audio.addEventListener("ended", () => resolve());
      audio.addEventListener("error", (e) => reject(e));
      audio.play().catch(reject);
    } catch (e) {
      reject(e);
    }
  });
}
